import { Car, Flame, Hammer, Sparkles, SprayCan, Trophy, Wrench } from "lucide-react";

export const SectionHead = ({ eyebrow, title, dark = true, children }) => (
  <div className="mb-10 max-w-2xl sm:mb-14">
    <p className={`eyebrow ${dark ? "" : "!text-[#C8322B]"}`}>{eyebrow}</p>
    <h2 className={`font-display mt-2 text-4xl sm:text-5xl lg:text-6xl ${dark ? "text-[#FAF7F2]" : "text-[#121214]"}`}>{title}</h2>
    {children}
  </div>
);

export const WhyChoose = ({ points }) => (
  <section id="why-choose-us" className="section-anchor relative bg-[#121214]" data-testid="why-choose-us-section">
    <div className="stripe-rule" />
    <div className="mx-auto max-w-7xl px-4 py-16 sm:px-8 sm:py-24">
      <SectionHead eyebrow="The Jimmyz difference" title="Why Choose Us" />
      <ol className="grid grid-cols-1 gap-x-12 gap-y-1 md:grid-cols-2" data-testid="why-choose-us-list">
        {points.map((p, i) => (
          <li
            key={p.id}
            className="group flex items-start gap-5 border-b border-zinc-800 py-5 transition-colors hover:border-[#FFD23F]/50"
            data-testid={`why-point-${i}`}
          >
            <span className="font-display w-10 shrink-0 text-3xl leading-none text-[#FFD23F]">{String(i + 1).padStart(2, "0")}</span>
            <span className="pt-0.5 text-base leading-relaxed text-zinc-200 sm:text-lg">{p.text}</span>
          </li>
        ))}
      </ol>
    </div>
  </section>
);

const ICON_RULES = [
  [/correct|polish|buff|detail|swirl/i, Sparkles],
  [/show|award|trophy/i, Trophy],
  [/fabricat|weld|metal/i, Flame],
  [/restor|classic|vintage/i, Car],
  [/collision|dent|body/i, Hammer],
  [/paint|spray|color|colour/i, SprayCan],
];
const iconFor = (title) => ICON_RULES.find(([re]) => re.test(title))?.[1] || Wrench;

export const ServicesSection = ({ services }) => (
  <section id="services" className="section-anchor relative overflow-hidden bg-[#121214]" data-testid="services-section">
    <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(200,50,43,0.35),transparent_55%),radial-gradient(ellipse_at_bottom_right,rgba(196,138,60,0.18),transparent_50%)]" />
    <div className="grain pointer-events-none absolute inset-0" />
    <div className="relative mx-auto max-w-7xl px-4 py-16 sm:px-8 sm:py-24">
      <SectionHead eyebrow="What we do" title="Services" />
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3" data-testid="services-grid">
        {services.map((s, i) => {
          const Icon = iconFor(s.title);
          return (
            <article
              key={s.id}
              className="group relative overflow-hidden rounded-lg border border-zinc-800 bg-[#1A1A1E]/90 p-6 pt-8 transition-[transform,border-color,box-shadow] duration-200 hover:-translate-y-1 hover:border-[#FFD23F]/70 hover:shadow-[0_0_28px_rgba(255,210,63,0.18)]"
              data-testid={`service-card-${i}`}
            >
              <span className="absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r from-[#C8322B] via-[#e0483f] to-[#C8322B]" />
              <div className="flex items-start gap-4">
                <span className="grid h-14 w-14 shrink-0 place-items-center rounded-full bg-[#FFD23F] text-[#121214] shadow-[0_0_0_4px_rgba(255,210,63,0.15)]">
                  <Icon className="h-7 w-7" strokeWidth={2} />
                </span>
                <div className="min-w-0">
                  <span className="font-display text-sm tracking-widest text-[#C48A3C]">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="font-sub break-words text-xl font-semibold uppercase leading-tight tracking-wide text-[#FFD23F]" data-testid="service-card-title">{s.title}</h3>
                </div>
              </div>
              {s.description && <p className="mt-4 break-words text-[15px] leading-relaxed text-zinc-300" data-testid="service-card-description">{s.description}</p>}
            </article>
          );
        })}
      </div>
    </div>
  </section>
);
